import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

const CartContext = createContext(null);
const KEY = 'mandispot_cart_v1';
const INDIA_KEY = 'bharatcart_india_cart_v1';

export function CartProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' && localStorage.getItem(KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {
      console.warn('[cart] failed to hydrate from localStorage', e);
    }
    return { store: null, items: [] };
  });
  const [india, setIndia] = useState(() => {
    try { return JSON.parse(localStorage.getItem(INDIA_KEY)) || { items: [] }; } catch { return { items: [] }; }
  });
  useEffect(() => { try { localStorage.setItem(INDIA_KEY, JSON.stringify(india)); } catch { /* storage full */ } }, [india]);

  // National (multi-seller) cart: each line keeps its own seller store
  const indiaAdd = useCallback((store, product) => {
    setIndia(prev => {
      const items = [...prev.items];
      const idx = items.findIndex(i => i.product.id === product.id);
      if (idx >= 0) items[idx] = { ...items[idx], qty: Math.min(items[idx].qty + 1, product.stock || 99) };
      else items.push({ product, store, qty: 1 });
      return { items };
    });
  }, []);
  const indiaSetQty = useCallback((productId, qty) => {
    setIndia(prev => ({ items: prev.items.map(i => i.product.id === productId ? { ...i, qty } : i).filter(i => i.qty > 0) }));
  }, []);
  const indiaClear = useCallback(() => setIndia({ items: [] }), []);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(state)); }
    catch (e) { console.warn('[cart] failed to persist to localStorage', e); }
  }, [state]);

  const setStoreIfEmpty = useCallback((store) => {
    setState(prev => prev.store ? prev : { store, items: [] });
  }, []);

  const addItem = useCallback((store, product) => {
    setState(prev => {
      if (prev.store && prev.store.id !== store.id) {
        // Replace cart with a new store
        return { store, items: [{ product, qty: 1 }] };
      }
      const items = [...prev.items];
      const idx = items.findIndex(i => i.product.id === product.id);
      if (idx >= 0) items[idx] = { ...items[idx], qty: items[idx].qty + 1 };
      else items.push({ product, qty: 1 });
      return { store, items };
    });
  }, []);

  const setQty = useCallback((productId, qty) => {
    setState(prev => {
      const items = prev.items.map(i => i.product.id === productId ? { ...i, qty } : i).filter(i => i.qty > 0);
      const store = items.length ? prev.store : null;
      return { store, items };
    });
  }, []);

  const clear = useCallback(() => setState({ store: null, items: [] }), []);

  const replaceCart = useCallback((store, items) => {
    setState({ store, items });
  }, []);

  const totals = useMemo(() => {
    const subtotal = state.items.reduce((s, i) => s + i.product.price * i.qty, 0);
    const delivery = state.store?.delivery_fee || 0;
    const count = state.items.reduce((s, i) => s + i.qty, 0);
    const indiaCount = india.items.reduce((s, i) => s + i.qty, 0);
    const indiaSubtotal = india.items.reduce((s, i) => s + i.product.price * i.qty, 0);
    return { subtotal, delivery, total: subtotal + delivery, count, indiaCount, indiaSubtotal, allCount: count + indiaCount };
  }, [state, india]);

  const value = useMemo(
    () => ({ ...state, addItem, setQty, clear, totals, setStoreIfEmpty, replaceCart, india: india.items, indiaAdd, indiaSetQty, indiaClear }),
    [state, addItem, setQty, clear, totals, setStoreIfEmpty, replaceCart, india, indiaAdd, indiaSetQty, indiaClear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
